<template>
  <main-template>
    <v-row>
      <v-col>
        <h1>
          <v-btn
            color="primary"
            x-small
            text
            @click="() => $router.back()"
            :disabled="submittingForm"
          >
            <v-icon left>fa fa-angle-left</v-icon>
          </v-btn>
          {{ editing ? "Editando" : "Criando" }} Documento
        </h1>
      </v-col>
    </v-row>

    <ValidationObserver v-slot="{ handleSubmit }">
      <form @submit.prevent="handleSubmit(submitForm)">
        <v-row>
          <v-col cols="6">
            <e-label>Disciplina</e-label>
            <ValidationProvider name="Disciplina" rules="required" v-slot="{ errors }">
              <e-autocomplete
                :items="filteredDisciplinas"
                v-model="form.disciplina_id"
                item-value="id"
                item-text="descricao"
                label="Selecione uma disciplina"
                solo
                dense
                :error-messages="errors"
              />
            </ValidationProvider>
          </v-col>

          <v-col cols="6">
            <e-label>Etapa</e-label>
            <ValidationProvider name="Etapa" rules="required" v-slot="{ errors }">
              <v-select
                :items="etapasList"
                :item-text="(etapa) => `${etapa.descricao} - ${etapa.circuito.descricao}`"
                item-value="id"
                v-model="form.etapa_id"
                label="Selecione uma etapa"
                solo
                dense
                :error-messages="errors"
                no-data-text="Nenhuma etapa 'entrada' no circuito atual"
              />
            </ValidationProvider>
          </v-col>
          <v-col cols="12">
            <ValidationProvider name="Series" rules="required" v-slot="{ errors }">
              <e-autocomplete
                :items="filteredSeries"
                v-model="form.series"
                multiple
                item-value="id"
                item-text="descricao"
                label="Selecione as séries"
                solo
                dense
                :error-messages="errors"
              />
            </ValidationProvider>
          </v-col>
          <v-col cols="12">
            <e-label>Arquivo PDF</e-label>
            <ValidationProvider
              name="Arquivo PDF"
              :rules="editing ? 'size:4096' : 'required|size:4096'"
              v-slot="{ errors }"
            >
              <v-file-input
                v-model="form.pdf"
                accept=".pdf"
                label="Selecione o PDF"
                outlined
                dense
                :required="!editing"
                :error-messages="errors"
              />
            </ValidationProvider>
            <div v-if="editing && form.filename && !form.pdf">
              Arquivo atual:
              <v-btn x-small @click="baixarPdf(documentoId)" class="ml-2" color="red lighten-5">
                <span class="mr-2">{{ form.filename }}</span>
                <v-icon color="red lighten-1" small> fa fa-file-pdf</v-icon>
              </v-btn>
            </div>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <div class="d-flex">
              <v-btn
                color="primary"
                :disabled="submittingForm"
                :loading="submittingForm"
                type="submit"
              >
                Enviar Dados
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </form>
    </ValidationObserver>
  </main-template>
</template>

<script>
import { mapActions } from "vuex";
import ELabel from "@/components/ELabel.vue";

export default {
  components: { ELabel },
  props: {
    editing: Boolean,
    documentoId: Number,
  },
  data() {
    return {
      submittingForm: false,
      form: {
        pdf: null, // Arquivo selecionado
        disciplina_id: null,
        etapa_id: null,
        series: [], // Array de IDs
        filename: null,
      },
      seriesList: [],
      disciplinasList: [],
      etapasList: [],
    };
  },
  computed: {
    // 🔹 Apenas séries que possuem pelo menos 1 disciplina com permite_plano = 1
    filteredSeries() {
      let seriesPermitidas = this.seriesList.filter((serie) =>
        serie.disciplinas?.some((d) => d.pivot?.permite_plano === 1)
      );

      // Se disciplina já foi escolhida, filtra cruzado
      if (this.form.disciplina_id) {
        seriesPermitidas = seriesPermitidas.filter((serie) =>
          serie.disciplinas?.some(
            (d) => d.id === this.form.disciplina_id && d.pivot?.permite_plano === 1
          )
        );
      }

      return seriesPermitidas;
    },

    // 🔹 Apenas disciplinas que permitem plano nas séries disponíveis
    filteredDisciplinas() {
      const disciplinasPermitidas = new Map();

      this.seriesList.forEach((serie) => {
        const disciplinas = serie.disciplinas || [];

        disciplinas
          .filter((d) => d.pivot?.permite_plano === 1)
          .forEach((d) => {
            disciplinasPermitidas.set(d.id, d);
          });
      });

      let lista = Array.from(disciplinasPermitidas.values());

      // Se séries foram selecionadas, filtra cruzado
      if (this.form.series.length) {
        const idsPermitidos = new Set();

        this.seriesList
          .filter((serie) => this.form.series.includes(serie.id))
          .forEach((serie) => {
            const disciplinas = serie.disciplinas || [];

            disciplinas
              .filter((d) => d.pivot?.permite_plano === 1)
              .forEach((d) => idsPermitidos.add(d.id));
          });

        lista = lista.filter((d) => idsPermitidos.has(d.id));
      }

      return lista;
    },
  },
  mounted() {
    this.loadData();
    if (this.editing) {
      this.loadDocumento();
    }
  },
  methods: {
    async loadData() {
      this.seriesList = await this.$services.seriesService.syncAll();

      this.disciplinasList = await this.$services.disciplinasService.syncAll();
      this.etapasList = await this.$services.etapasService.getEtapasEntrada();
    },

    async loadDocumento() {
      const documento = await this.$services.documentosPlanosService.buscarPorId(
        parseInt(this.documentoId, 10)
      );
      if (documento) {
        this.form = {
          disciplina_id: documento.disciplina_id,
          etapa_id: documento.etapa_id,
          series: documento.series.map((s) => s.id),
          pdf: null,
          filename: documento.filename,
        };
      }
    },

    onFileChange(e) {
      this.form.caminho_pdf = e.target.files[0];
    },
    async submitForm() {
      this.submittingForm = true;

      const isUpdatingFile = this.form.pdf instanceof File;

      if (!this.editing || isUpdatingFile) {
        if (!this.form.pdf) {
          this.$toast.danger("Selecione um arquivo PDF.");
          this.submittingForm = false;
          return;
        }
      }

      const formData = new FormData();

      if (isUpdatingFile) {
        formData.append("file", this.form.pdf);
      }

      formData.append("disciplina_id", this.form.disciplina_id);
      formData.append("etapa_id", this.form.etapa_id);

      this.form.series.forEach((id) => {
        formData.append("series[]", id);
      });
      try {
        if (this.editing) {
          await this.$services.documentosPlanosService.atualizar(this.documentoId, formData);
          this.$toast.success("Documento atualizado com sucesso");
        } else {
          await this.$services.documentosPlanosService.salvar(formData);
          this.$toast.success("Documento criado com sucesso");
        }
        this.$router.push({ name: "gerenciarPlanos" });
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.submittingForm = false;
      }
    },
    async baixarPdf(matricula_id) {
      this.$loaderService.open("Baixando...");
      try {
        await this.$services.documentosPlanosService.baixarPdf(parseInt(this.documentoId, 10));
      } catch (error) {
        this.$handleError(error);
      }
      this.$loaderService.close();
    },
  },
  watch: {
    filteredSeries(newList) {
      this.form.series = this.form.series.filter((id) => newList.some((s) => s.id === id));
    },

    filteredDisciplinas(newList) {
      if (this.form.disciplina_id && !newList.some((d) => d.id === this.form.disciplina_id)) {
        this.form.disciplina_id = null;
      }
    },
  },
};
</script>

<style scoped></style>
